import classNames from "classnames";
import { X } from "lucide-react";
import * as React from "react";

import { RobotoButton, type RobotoButtonProps } from "../RobotoButton";

import styles from "./Banner.module.css";

/**
 * 1. `info` is informational
 * 2. `warning` needs care
 * 3. `error` is a failure
 * 4. `success` is something that finished well
 */
export type BannerVariant = "info" | "warning" | "error" | "success";

export type BannerProps = Omit<
  React.ComponentPropsWithoutRef<"div">,
  "title" | "children"
> & {
  /** @default "info" */
  variant?: BannerVariant;
  /** Rendered before the content. Decorative: give it `aria-hidden`. */
  icon?: React.ReactNode;
  /** The heading. With `title` or `description` the banner is structured. */
  title?: string;
  /** Under the title. */
  description?: React.ReactNode;
  /**
   * The trailing call to action, usually one or more `Banner.Action`s in a
   * Fragment. Structured mode only: ignored when the banner has neither
   * `title` nor `description`.
   */
  action?: React.ReactNode;
  /** Simple mode: one line of content and no title. Wins over `children`. */
  text?: string;
  /** Simple mode content, when `text` is not given. */
  children?: React.ReactNode;
  /**
   * Called when the reader closes the banner. When given, a close button
   * follows the content, in either mode; the caller stops rendering the
   * banner.
   */
  onDismiss?: () => void;
};

/**
 * A message about the page or the thing on it, set in a tinted box: a
 * disabled resource, a failed load. Announced politely as a `status`; pass
 * `role="alert"` for one that must interrupt.
 *
 * @example
 * ```tsx
 * <Banner variant="warning" icon={<PauseCircle aria-hidden />}
 *   title="This trigger is disabled"
 *   description="It won't evaluate new events until it's enabled again." />
 * <Banner variant="error" text={error.message} />
 * ```
 */
function Root(props: BannerProps) {
  const {
    variant = "info",
    icon,
    title,
    description,
    action,
    text,
    children,
    onDismiss,
    className,
    ...rest
  } = props;

  const structured = title !== undefined || description !== undefined;
  const trailingAction = structured ? action : undefined;

  return (
    <div
      role="status"
      {...rest}
      className={classNames(styles.root, className)}
      data-variant={variant}
    >
      <div className={styles.body}>
        {icon !== undefined && <span className={styles.icon}>{icon}</span>}
        {structured ? (
          <div className={styles.content}>
            {title !== undefined && <p className={styles.title}>{title}</p>}
            {description !== undefined && (
              <div className={styles.description}>{description}</div>
            )}
          </div>
        ) : (
          <div className={styles.message}>{text ?? children}</div>
        )}
      </div>
      {(trailingAction !== undefined || onDismiss !== undefined) && (
        <div className={styles.action}>
          {trailingAction}
          {onDismiss !== undefined && (
            <RobotoButton
              variant="ghost"
              icon={<X aria-hidden />}
              aria-label="Dismiss"
              onClick={onDismiss}
            />
          )}
        </div>
      )}
    </div>
  );
}

export type BannerActionProps = Omit<RobotoButtonProps, "variant" | "size"> & {
  /** @default "ghost" */
  variant?: "secondary" | "ghost";
};

/** A button in a banner's `action` slot. */
function Action({ variant = "ghost", ...props }: BannerActionProps) {
  return <RobotoButton {...(props as RobotoButtonProps)} variant={variant} />;
}

export const Banner = Object.assign(Root, { Action });
